import { z } from 'zod';
import { EmailSchema, PasswordSchema, UuidSchema } from './common.js';
import { USER_ROLES } from '../types/user.js';

export const LoginRequestSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Server response from POST /auth/login and /auth/refresh. */
export const AuthResponseSchema = z.object({
  accessToken: z.string(),
  user: z.object({
    id: UuidSchema,
    email: EmailSchema,
    fullName: z.string(),
    role: z.enum([USER_ROLES[0], ...USER_ROLES.slice(1)] as [string, ...string[]]),
    tenantId: UuidSchema.nullable(),
  }),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

/** Used only internally for changing passwords (Phase 5+). Included for Zod consistency. */
export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: PasswordSchema,
});

// ---- Team invitations (Phase 5) ----

/** Roles someone may be invited as. Never includes super_admin (DB CHECK enforces this too). */
export const INVITABLE_ROLES = ['admin', 'consultant'] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

/** Body of POST /admin/team/invitations. */
export const CreateInvitationSchema = z.object({
  email: EmailSchema,
  role: z.enum(INVITABLE_ROLES),
});
export type CreateInvitationInput = z.infer<typeof CreateInvitationSchema>;

/** Body of POST /auth/invitations/:token/accept. */
export const AcceptInvitationSchema = z.object({
  full_name: z.string().trim().min(1, 'Your name is required').max(200),
  password: PasswordSchema,
});
export type AcceptInvitationInput = z.infer<typeof AcceptInvitationSchema>;

// ---- Self-serve agency signup (Phase 10, Stage 2) ----

/** Body of POST /auth/agency-signup (public self-serve). */
export const AgencySignupSchema = z.object({
  agency_name: z.string().trim().min(1, 'Agency name is required').max(200),
  admin_full_name: z.string().trim().min(1, 'Your name is required').max(200),
  email: EmailSchema,
  password: PasswordSchema,
  // Acceptance is mandatory: only the literal `true` passes. The errorMap turns
  // the default "invalid literal" message into something a user can act on.
  tos_accepted: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Service to continue' }),
  }),
  privacy_accepted: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Privacy Policy to continue' }),
  }),
});
export type AgencySignupInput = z.infer<typeof AgencySignupSchema>;

/** Body of POST /auth/agency-signup/verify. */
export const VerifyAgencySignupSchema = z.object({
  token: z.string().min(1, 'Verification token is required'),
});
export type VerifyAgencySignupInput = z.infer<typeof VerifyAgencySignupSchema>;

/**
 * Body of POST /admin/agencies — a super-admin creating an agency by hand.
 * No password: the new admin sets their own via the emailed invite link.
 */
export const CreateAgencySchema = z.object({
  agency_name: z.string().trim().min(1, 'Agency name is required').max(200),
  admin_full_name: z.string().trim().min(1, "The admin's name is required").max(200),
  email: EmailSchema,
});
export type CreateAgencyInput = z.infer<typeof CreateAgencySchema>;

/**
 * Body of POST /admin/agencies/:tenantId/invite-admin — a super-admin placing a
 * first admin into an existing tenant (the zero-admin rescue). Email only: the
 * role is fixed to 'admin' server-side, and the accept flow collects the name
 * and password.
 */
export const InviteAgencyAdminSchema = z.object({
  email: EmailSchema,
});
export type InviteAgencyAdminInput = z.infer<typeof InviteAgencyAdminSchema>;
